import type { ReactElement } from "react";
import { useCallback, useState } from "react";

import { translate } from "app/filters/i18nFilter";
import { ComboboxWithAsyncSelectAll } from "app/pages/sales-intelligence/common-components/dropdown/ComboboxWithAsyncSelectAll";
import type { IGenericDropDownItem } from "app/pages/sales-intelligence/common-components/dropdown/types";
import type { ISiteTag } from "app/pages/sales-intelligence/domain/leads/types/website";
import leadsSearchFiltersTrackingService from "app/pages/sales-intelligence/modules/leads-search-filters/services/tracking/trackingService";
import useSiteTagsAutocompleteQuery from "app/pages/sales-intelligence/query/queries/useSiteTagsAutocompleteQuery";

import useDebouncedValue from "../../../hooks/useDebouncedValue";

const SELECT_ALL_TRACKING_ID = "__SELECT_ALL__";
const SEARCH_SIZE = 35;

const selectTagItems = (data: readonly ISiteTag[] | undefined): IGenericDropDownItem<string>[] =>
    (data ?? []).map(({ tag }) => ({ id: tag, text: tag }));

interface ISiteTagsDropdownProps {
    values: readonly string[];
    disabledValues?: readonly string[];
    onChange: (tagIds: readonly string[]) => void;
}

const ValuesDropdown = ({ values, disabledValues = [], onChange }: ISiteTagsDropdownProps): ReactElement => {
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedTerm = useDebouncedValue(searchTerm);

    const { data: items = [], isFetching } = useSiteTagsAutocompleteQuery(debouncedTerm, SEARCH_SIZE, selectTagItems);

    const handleSelectionChange = useCallback(
        (newSelection: readonly string[], isAllSelected: boolean) => {
            if (isAllSelected) {
                const allLoadedIds = items.map((item) => item.id);
                const wasAllSelected = allLoadedIds.length > 0 && allLoadedIds.every((id) => values.includes(id));

                if (wasAllSelected) {
                    leadsSearchFiltersTrackingService.filters.trackSiteTagsValueDelete(SELECT_ALL_TRACKING_ID);
                } else {
                    leadsSearchFiltersTrackingService.filters.trackSiteTagsValueAdd(SELECT_ALL_TRACKING_ID);
                }
            } else {
                const added = newSelection.filter((id) => !values.includes(id));
                const removed = values.filter((id) => !newSelection.includes(id));

                added.forEach(leadsSearchFiltersTrackingService.filters.trackSiteTagsValueAdd);
                removed.forEach(leadsSearchFiltersTrackingService.filters.trackSiteTagsValueDelete);
            }
            onChange(newSelection);
        },
        [values, items, onChange],
    );

    return (
        <ComboboxWithAsyncSelectAll
            dataAutomation="filter-panel.site-tags.dropdown"
            items={items}
            selectedItems={values}
            disabledItems={disabledValues}
            buttonText={translate("si.lead_gen_filters.categoryFilter.tags.dd_button_text")}
            searchPlaceholder={translate("si.lead_gen_filters.categoryFilter.tags.dd_placeholder")}
            selectAllLabel={translate("si.lead_gen_filters.categoryFilter.tags.select_all")}
            searchTerm={searchTerm}
            isLoading={isFetching}
            hideArrow
            withEmptyState={debouncedTerm.length ? !isFetching : false}
            onSelectionChange={handleSelectionChange}
            onSearchChange={setSearchTerm}
        />
    );
};

export default ValuesDropdown;
